import { WorkoutData, SleepMetrics, RecoveryMetrics, User, WorkoutRecommendation } from '../types';
import { PlannedWorkout } from './trainingPlan';

export const generateWorkoutRecommendation = (
  user: User,
  workoutHistory: WorkoutData[],
  sleepMetrics: SleepMetrics | null,
  recoveryMetrics: RecoveryMetrics | null,
  checkIn?: { feeling: 'rough' | 'okay' | 'strong' } | null,
  plannedToday?: PlannedWorkout | null
): WorkoutRecommendation => {
  // Metrics can be missing entirely (nothing connected, or every read
  // failed today). Reason without them rather than substituting numbers.
  const hasMetrics = !!sleepMetrics && !!recoveryMetrics;

  // Prefer Oura's own readiness score when the ring is connected; fall back
  // to our estimate computed from the individual metrics.
  const isOuraReadiness = recoveryMetrics?.readinessScore !== undefined;
  const readinessScore = hasMetrics
    ? recoveryMetrics.readinessScore ?? calculateReadinessScore(sleepMetrics, recoveryMetrics)
    : null;
  const recentTrainingLoad = calculateTrainingLoad(workoutHistory);

  // Oura's scale treats <70 as "pay attention"; our estimate runs lower,
  // so it keeps the original conservative cutoff.
  const recoveryThreshold = isOuraReadiness ? 70 : 50;

  // A strongly elevated body temperature suggests illness — recovery day
  // regardless of what the readiness score says. (Found by the eval suite:
  // readiness alone misses this.) Wearables report the deviation in °C;
  // only display copy converts to °F.
  const feverish = (recoveryMetrics?.bodyTemperature ?? 0) >= 0.8;

  // The athlete's own check-in outranks good-looking metrics in the
  // cautious direction: feeling rough caps today at recovery. (Feeling
  // strong never escalates past what the metrics support.)
  const feltRough = checkIn?.feeling === 'rough';

  // An external plan (Runna etc.) decides WHAT today is; the safety gates
  // above/below still decide WHETHER the athlete is ready for it. With no
  // metrics there is nothing to gate on, so the check-in alone decides.
  const greenLight =
    !feverish && !feltRough && (readinessScore === null || readinessScore >= recoveryThreshold);

  const baseRecommendation = feverish
    ? {
        type: 'Recovery',
        duration: 20,
        intensity: 'low' as const,
        details: 'Rest or gentle walking only — your body temperature is elevated, which often precedes illness.',
      }
    : feltRough
      ? {
          type: 'Recovery',
          duration: 30,
          intensity: 'low' as const,
          details: 'Easy movement only — a light walk, gentle mobility, or full rest if that feels right.',
        }
      : plannedToday && greenLight
        ? plannedWorkoutToRecommendation(plannedToday)
        : determineWorkoutType(user, readinessScore, recoveryThreshold);
  const alternativeOptions = generateAlternatives(baseRecommendation);
  const planNote = plannedToday && !greenLight
    ? ` Your training plan has "${plannedToday.title}" scheduled — consider swapping it with an easier day rather than pushing through.`
    : '';
  const reasoning = feverish
    ? `Your body temperature is ${((recoveryMetrics?.bodyTemperature ?? 0) * 1.8).toFixed(1)}°F above baseline, which often signals oncoming illness — today is a recovery day regardless of your other metrics. If you feel unwell, rest completely.${planNote}`
    : feltRough
      ? `You checked in feeling rough this morning, so today stays easy regardless of the numbers — how you feel is real training data, and recovering when your body asks for it is what keeps the streak alive.${planNote}`
      : plannedToday && greenLight
        ? readinessScore === null
          ? `No recovery data came through today, so today follows your training plan as written — back off if it feels harder than it should.`
          : `Your recovery looks good (readiness ${Math.round(readinessScore)}), so today follows your training plan as written.`
        : generateReasoning(
            baseRecommendation,
            readinessScore,
            isOuraReadiness,
            recentTrainingLoad
          ) + planNote;

  return {
    ...baseRecommendation,
    alternativeOptions,
    reasoning,
  };
};

/** Turn a calendar-feed plan entry into a recommendation. The feed is
    natural language, so duration/intensity are best-effort reads of the
    text (e.g. "Tempo • 3.2mi • 30m - 35m"). */
const plannedWorkoutToRecommendation = (
  planned: PlannedWorkout
): Omit<WorkoutRecommendation, 'alternativeOptions' | 'reasoning'> => {
  const text = `${planned.title}\n${planned.details ?? ''}`;

  // Take the upper bound of a "30m - 35m" / "1h10m - 1h20m" style range.
  const durations = [...text.matchAll(/(?:(\d+)\s*h\s*)?(\d+)\s*m(?:in)?\b/gi)]
    .map((m) => (m[1] ? parseInt(m[1]) * 60 : 0) + parseInt(m[2]))
    .filter((mins) => mins >= 10 && mins <= 300);
  const duration = durations.length ? Math.max(...durations) : 45;

  // Classify from the title and summary line only — deeper in, exercise
  // names ("Toe Walks", "Easy Run pace") false-match the keywords.
  const headline = `${planned.title} ${planned.details?.split('\n')[0] ?? ''}`;
  const intensity = /tempo|interval|speed|track|progressi|race pace|hill|threshold|over and under/i.test(headline)
    ? ('high' as const)
    : /easy|recovery|stretch|mobility|yoga|walk|stability/i.test(headline)
      ? ('low' as const)
      : ('moderate' as const);

  return {
    type: planned.title.replace(/^[^\p{L}\p{N}]+/u, '').trim() || planned.title,
    duration,
    intensity,
    details:
      planned.details?.split('📲')[0].trim().slice(0, 600) ||
      "Follow today's session from your training plan.",
  };
};

const calculateReadinessScore = (
  sleep: SleepMetrics,
  recovery: RecoveryMetrics
): number => {
  const sleepScore = sleep.quality * 0.4;
  const hrvScore = normalizeHRV(recovery.hrv) * 0.3;
  const rhrScore = normalizeRHR(recovery.restingHeartRate) * 0.3;
  
  return sleepScore + hrvScore + rhrScore;
};

const calculateTrainingLoad = (workoutHistory: WorkoutData[]): number => {
  return workoutHistory.reduce((load, workout) => {
    const intensityMultiplier = 
      workout.intensity === 'high' ? 2 :
      workout.intensity === 'moderate' ? 1.5 : 1;
    
    return load + (workout.duration / 3600) * intensityMultiplier;
  }, 0);
};

const determineWorkoutType = (
  user: User,
  readinessScore: number | null,
  recoveryThreshold: number
): Omit<WorkoutRecommendation, 'alternativeOptions' | 'reasoning'> => {
  const { primary: goal } = user.fitnessGoals;

  // Default recovery workout when readiness is low. A null score means no
  // metrics today, which is not evidence of poor recovery — plan normally.
  if (readinessScore !== null && readinessScore < recoveryThreshold) {
    return {
      type: 'Recovery',
      duration: 30,
      intensity: 'low',
      details: 'Light mobility work and stretching to promote recovery',
    };
  }

  // Workout recommendations based on fitness goals
  const workouts: Record<string, Omit<WorkoutRecommendation, 'alternativeOptions' | 'reasoning'>> = {
    'strength': {
      type: 'Strength Training',
      duration: 60,
      intensity: 'high',
      details: 'Full-body strength training with compound exercises',
    },
    'endurance': {
      type: 'Endurance Training',
      duration: 45,
      intensity: 'moderate',
      details: 'Zone 2 cardio training',
    },
    'weight-loss': {
      type: 'HIIT',
      duration: 40,
      intensity: 'high',
      details: 'High-intensity interval training with body weight exercises',
    },
    'general-fitness': {
      type: 'Mixed Training',
      duration: 50,
      intensity: 'moderate',
      details: 'Combination of strength and cardio exercises',
    },
  };

  // Ensure we always return a valid workout recommendation
  return workouts[goal] || workouts['general-fitness'];
};

const generateAlternatives = (
  base: Omit<WorkoutRecommendation, 'alternativeOptions' | 'reasoning'>
): { lowEnergy: string; highEnergy: string } => {
  // Ensure base.duration exists and is a number
  const duration = typeof base.duration === 'number' ? base.duration : 30;
  
  return {
    lowEnergy: `${Math.floor(duration * 0.7)}-minute ${base.type} at reduced intensity`,
    highEnergy: `${Math.floor(duration * 1.3)}-minute ${base.type} with increased intensity`,
  };
};

const generateReasoning = (
  recommendation: Omit<WorkoutRecommendation, 'alternativeOptions' | 'reasoning'>,
  readinessScore: number | null,
  isOuraReadiness: boolean,
  trainingLoad: number
): string => {
  const source =
    readinessScore === null
      ? `No recovery data came through today (your wearable didn't report in)`
      : isOuraReadiness
        ? `Your Oura readiness score is ${Math.round(readinessScore)}`
        : `Your estimated readiness is ${Math.round(readinessScore)} (connect an Oura ring for a more precise score)`;
  const load = trainingLoad > 0
    ? `and you've logged about ${trainingLoad.toFixed(1)} intensity-weighted hours of training in the past week`
    : `and you have no logged training in the past week`;
  return `${source} ${load}, so we recommend a ${recommendation.intensity}-intensity ${recommendation.type.toLowerCase()} session to keep you progressing without cutting into recovery.`;
};

const normalizeHRV = (hrv: number): number => {
  // Normalize HRV to 0-100 scale based on typical ranges
  return Math.min(100, Math.max(0, (hrv - 20) * (100 / 80)));
};

const normalizeRHR = (rhr: number): number => {
  // Normalize RHR to 0-100 scale (inverse relationship - lower is better)
  return Math.min(100, Math.max(0, (100 - (rhr - 40)) * (100 / 40)));
};